import type { CalendarProvider } from '@plura/shared/types';
import { env } from './env';

/**
 * Whether users can start a new OAuth connect flow for each scheduler provider.
 * Existing workspace connections still work at runtime when disabled here.
 *
 * - Google Calendar is env-gated (see `isGoogleCalendarConnectEnabled`): off in
 *   production until Google verifies the OAuth app, always on elsewhere.
 */
const STATIC_CONNECT_ENABLED: Record<CalendarProvider, boolean> = {
  CALENDLY: true,
  CALCOM: true,
  GOOGLE_CALENDAR: false,
};

/**
 * Google Calendar's dedicated OAuth app is pending Google verification. While
 * unverified the project stays in "Testing" mode, so any non-test user who tries
 * to connect hits Google's hard "Access blocked" error. So we only expose the
 * new-connect flow when it's safe:
 *   - automatically outside production (local / staging dogfooding), or
 *   - in production when NEXT_PUBLIC_GOOGLE_CALENDAR_CONNECT_ENABLED is "1"/"true"
 *     — flip that env var + redeploy the day verification is approved.
 *
 * Existing connections keep working regardless; this only gates new connects.
 */
function isGoogleCalendarConnectEnabled(): boolean {
  if (env.NEXT_PUBLIC_APP_ENV !== 'production') return true;
  const flag = env.NEXT_PUBLIC_GOOGLE_CALENDAR_CONNECT_ENABLED;
  return flag === '1' || flag === 'true';
}

/**
 * Static baseline, kept for backward-compat with direct importers. NOTE: the
 * real Google Calendar value is dynamic — always read it via
 * `isSchedulerProviderConnectEnabled('GOOGLE_CALENDAR')`, not this map.
 */
export const SCHEDULER_PROVIDER_CONNECT_ENABLED: Record<CalendarProvider, boolean> =
  STATIC_CONNECT_ENABLED;

export function isSchedulerProviderConnectEnabled(provider: CalendarProvider): boolean {
  if (provider === 'GOOGLE_CALENDAR') return isGoogleCalendarConnectEnabled();
  return STATIC_CONNECT_ENABLED[provider] ?? false;
}
